import { ErrorBoundary, VStack } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback } from 'react';

import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { ReportGeneratorSubCategory } from '../../../../store/reportGenerator/categories';
import { useReportGeneratorRequest } from '../../../../store/reportGenerator/request';
import { RecentSameFileModal } from '../ReportGeneratorRequestModal/RecentSameFileModal';

import { DocumentsListingSection } from './DocumentsListingSection';
import { ReportCatalogSection } from './ReportCatalogSection';
import { ReportCatalogSkeleton } from './ReportCatalogSkeleton';

export const MainReportGenerator: React.FC = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const {
    categories,
    catalogCategoryFilterId,
    visibleSubCategories,
    showCategoryTabs,
    categoriesLoading,
    categoriesError,
    refreshCategories,
    setCatalogCategoryFilter,
    selectSubCategoryForRequest,
    recentSameReport,
    clearRecentSameReport,
    getReportingFileAndDownload,
    retryReportingFile,
  } = useReportGeneratorRequest();

  const sectionSpace = useBreakpointValue({
    base: 'stack.xxs',
    sm: 'stack.nano',
    lg: 'stack.xxs',
  });

  const handleOpenRequestModal = useCallback(
    (subCategory: ReportGeneratorSubCategory) => {
      if (!subCategory.canRequest) {
        return;
      }

      selectSubCategoryForRequest(subCategory);
      navigation.navigate('ReportGeneratorRequestModal');
    },
    [navigation, selectSubCategoryForRequest]
  );

  return (
    <>
      <VStack width="full" space={sectionSpace} flexShrink={0}>
        {categoriesLoading ? (
          <ReportCatalogSkeleton />
        ) : categoriesError ? (
          <ErrorBoundary
            onRefresh={refreshCategories}
            errorTitle="Não foi possível carregar as categorias"
            errorMessage="Tente novamente em alguns instantes."
          />
        ) : (
          <ReportCatalogSection
            categories={categories}
            catalogCategoryFilterId={catalogCategoryFilterId}
            visibleSubCategories={visibleSubCategories}
            showCategoryTabs={showCategoryTabs}
            onSelectCategoryFilter={setCatalogCategoryFilter}
            onGenerateReport={handleOpenRequestModal}
          />
        )}
        <DocumentsListingSection />
      </VStack>

      {recentSameReport ? (
        <RecentSameFileModal
          isOpen={Boolean(recentSameReport)}
          report={recentSameReport}
          onClose={clearRecentSameReport}
          getReportingFileAndDownload={getReportingFileAndDownload}
          retryReportingFile={retryReportingFile}
        />
      ) : null}
    </>
  );
};
